import { useMemo, useState } from 'react';
import { describeError } from '@pc/core';
import { Button, Card, Field, Rule, useCanWriteOrg } from '@pc/ui';
import type { OrderDetailSlotContext } from '@pc/modules-orders/slots';
import type { ProposalContractAcceptance } from '@pc/db';
import { useActiveCtx } from '../useCtx';
import {
  useAttachContractToOrder,
  useContractTemplates,
  useDetachContractFromOrder,
  useOrderAcceptances,
  useProposalContracts,
} from '../queries';

/**
 * Renders inside the orders module's OrderDetailPage via the
 * `order-detail.after-actions` slot registered in contracts/module.tsx.
 *
 * Lists every contract attached to the order, lets the operator attach or
 * detach (while the order is still in draft/declined), and surfaces the
 * client's signed/unsigned status + acceptance metadata.
 */
export function AttachedContractsCard(props: OrderDetailSlotContext) {
  const ctx = useActiveCtx();
  const canWrite = useCanWriteOrg();
  const attachedQ = useProposalContracts(ctx, props.orderId);
  const templatesQ = useContractTemplates(ctx, { includeArchived: false });
  const acceptancesQ = useOrderAcceptances(ctx, props.orderId);
  const attach = useAttachContractToOrder(ctx);
  const detach = useDetachContractFromOrder(ctx);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const acceptanceByContractId = useMemo(() => {
    const m = new Map<string, ProposalContractAcceptance>();
    for (const a of acceptancesQ.data ?? []) m.set(a.proposal_contract_id, a);
    return m;
  }, [acceptancesQ.data]);

  // canEdit gates the inline attach / detach affordances. The status check
  // already prevents edits on approved/invoiced/etc orders; the role check
  // additionally hides the affordances from `viewer` members so they don't
  // see a button that would 42501 on submit.
  const canEdit =
    canWrite && (props.status === 'draft' || props.status === 'declined');
  const availableTemplates = (templatesQ.data ?? []).filter(
    (t) => !(attachedQ.data ?? []).some((pc) => pc.template_id === t.id) && t.current_version > 0,
  );

  async function doAttach() {
    setError(null);
    if (!selectedTemplate) return;
    try {
      await attach.mutateAsync({ orderId: props.orderId, templateId: selectedTemplate });
      setSelectedTemplate('');
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <Card
      title="Attached contracts"
      hint={
        (attachedQ.data?.length ?? 0) > 0
          ? `${(attachedQ.data ?? []).length} attached`
          : 'None yet'
      }
    >
      {error ? <p className="text-sm text-clay mb-3">{error}</p> : null}

      {(attachedQ.data?.length ?? 0) === 0 ? (
        <p className="text-sm text-muted">
          No contracts attached. The client will only see what&apos;s listed above.
          {canEdit ? ' Attach one below to require their signature on approval.' : null}
        </p>
      ) : (
        <ul className="flex flex-col">
          {attachedQ.data!.map((pc, i) => {
            const acceptance = acceptanceByContractId.get(pc.id);
            return (
              <li key={pc.id}>
                {i > 0 ? <Rule className="my-3" /> : null}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-sm">{pc.title_snapshot}</div>
                    <div className="text-xs text-muted">
                      {pc.require_signature ? 'Signature required' : 'Acknowledgment only'} ·
                      Attached {new Date(pc.attached_at).toLocaleDateString()}
                    </div>
                    {acceptance ? (
                      <div className="text-xs text-olive mt-1">
                        Signed by <strong>{acceptance.acceptor_name}</strong> on{' '}
                        {new Date(acceptance.accepted_at).toLocaleString()}
                        {acceptance.acceptor_ip ? ` · ${acceptance.acceptor_ip}` : ''}
                      </div>
                    ) : (
                      <div className="text-xs text-clay mt-1">Awaiting client signature</div>
                    )}
                    <div className="text-[10px] text-muted tabular mt-1">
                      doc sha-256 {(pc.body_sha256 ?? '').slice(0, 16)}…
                    </div>
                  </div>
                  {canEdit && !acceptance ? (
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={async () => {
                        if (confirm(`Detach "${pc.title_snapshot}" from this proposal?`)) {
                          try {
                            await detach.mutateAsync({ id: pc.id, orderId: props.orderId });
                          } catch (e) {
                            setError(describeError(e));
                          }
                        }
                      }}
                    >
                      Detach
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && availableTemplates.length > 0 ? (
        <div className="mt-4 flex flex-col gap-2">
          <Rule />
          <Field label="Attach a contract">
            <div className="flex gap-2">
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="flex-1 bg-paper border border-rule-strong rounded-none px-3 py-2 text-sm text-ink"
              >
                <option value="">Select a published contract template…</option>
                {availableTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (v{t.current_version})
                  </option>
                ))}
              </select>
              <Button onClick={doAttach} disabled={!selectedTemplate || attach.isPending}>
                {attach.isPending ? 'Attaching...' : 'Attach'}
              </Button>
            </div>
          </Field>
        </div>
      ) : canEdit && templatesQ.data?.length === 0 ? (
        <p className="text-xs text-muted mt-3">
          No contract templates yet. Create one under <em>Contracts</em> in the nav.
        </p>
      ) : null}
    </Card>
  );
}
